import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  inject
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ToastService, Toast } from '@core/services/toast.service';

type ToastPosition = NonNullable<Toast['position']>;

@Component({
  selector: 'app-toast-container',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @for (position of positions; track position) {
      @if (toastsByPosition()[position].length > 0) {
        <div class="toast-container toast-container-{{ position }}">
          @for (toast of toastsByPosition()[position]; track toast.id) {
            <div class="toast toast-{{ toast.type }}">
              <div class="toast-content">
                <div class="toast-icon">
                  @switch (toast.type) {
                    @case ('success') {
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M16.25 5.625L7.5 14.375L3.75 10.625" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    }
                    @case ('error') {
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    }
                    @case ('warning') {
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10 6.25V10.625M10 14.375h.008M18.125 10C18.125 14.4873 14.4873 18.125 10 18.125C5.51269 18.125 1.875 14.4873 1.875 10C1.875 5.51269 5.51269 1.875 10 1.875C14.4873 1.875 18.125 5.51269 18.125 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    }
                    @case ('info') {
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10 13.125V10M10 6.875h.008M18.125 10C18.125 14.4873 14.4873 18.125 10 18.125C5.51269 18.125 1.875 14.4873 1.875 10C1.875 5.51269 5.51269 1.875 10 1.875C14.4873 1.875 18.125 5.51269 18.125 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    }
                  }
                </div>

                <div class="toast-text">
                  <div class="toast-message">{{ toast.message }}</div>
                </div>

                <button
                  class="toast-close"
                  (click)="toastService.remove(toast.id)"
                  aria-label="Close notification">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          }
        </div>
      }
    }
  `,
  styles: [`
    :host {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 2147483000;
    }

    .toast-container {
      position: absolute;
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: max(24px, env(safe-area-inset-top)) max(24px, env(safe-area-inset-right)) max(24px, env(safe-area-inset-bottom)) max(24px, env(safe-area-inset-left));
    }

    .toast-container-top-right {
      top: 0;
      right: 0;
      align-items: flex-end;
    }

    .toast-container-top-left {
      top: 0;
      left: 0;
      align-items: flex-start;
    }

    .toast-container-bottom-right {
      right: 0;
      bottom: 0;
      align-items: flex-end;
      flex-direction: column-reverse;
    }

    .toast-container-bottom-left {
      left: 0;
      bottom: 0;
      align-items: flex-start;
      flex-direction: column-reverse;
    }

    .toast-container-top-center {
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      align-items: center;
    }

    .toast-container-bottom-center {
      left: 50%;
      bottom: 0;
      transform: translateX(-50%);
      align-items: center;
      flex-direction: column-reverse;
    }

    .toast {
      pointer-events: auto;
      min-width: 320px;
      max-width: 400px;
      background: #ffffff !important;
      border-radius: 12px;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05);
      border: 1px solid rgba(0, 0, 0, 0.1);
      overflow: hidden;
      animation: slideIn 0.3s ease-out;
    }

    .toast-success {
      border-left: 4px solid #10b981;
    }

    .toast-error {
      border-left: 4px solid #ef4444;
    }

    .toast-warning {
      border-left: 4px solid #f59e0b;
    }

    .toast-info {
      border-left: 4px solid #3b82f6;
    }

    .toast-content {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 16px;
    }

    .toast-icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      margin-top: 2px;
    }

    .toast-success .toast-icon {
      color: #10b981;
    }

    .toast-error .toast-icon {
      color: #ef4444;
    }

    .toast-warning .toast-icon {
      color: #f59e0b;
    }

    .toast-info .toast-icon {
      color: #3b82f6;
    }

    .toast-text {
      flex: 1;
      min-width: 0;
    }

    .toast-title {
      font-weight: 600;
      font-size: 14px;
      line-height: 1.4;
      color: #111827;
      margin-bottom: 2px;
    }

    .toast-message {
      font-size: 13px;
      line-height: 1.4;
      color: #6b7280;
      word-wrap: break-word;
    }

    .toast-close {
      flex-shrink: 0;
      background: none;
      border: none;
      cursor: pointer;
      color: #9ca3af;
      padding: 2px;
      border-radius: 4px;
      transition: all 0.2s ease;
    }

    .toast-close:hover {
      color: #6b7280;
      background-color: #f3f4f6;
    }

    .toast-close:focus {
      outline: 2px solid #3b82f6;
      outline-offset: 2px;
    }

    /* Animations */
    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @media (max-width: 767px) {
      .toast-container {
        padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
        left: 0;
        right: 0;
        transform: none;
        align-items: stretch;
      }

      .toast {
        min-width: 100%;
        max-width: 100%;
      }
    }

    .toast {
      background: #ffffff !important;
      color: #111827 !important;
    }

    .toast-title {
      color: #111827 !important;
    }

    .toast-message {
      color: #6b7280 !important;
    }

    @media (prefers-contrast: high) {
      .toast {
        border-width: 2px;
      }

      .toast-success {
        border-left-width: 6px;
      }

      .toast-error {
        border-left-width: 6px;
      }

      .toast-warning {
        border-left-width: 6px;
      }

      .toast-info {
        border-left-width: 6px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .toast {
        animation: none;
      }

      .toast-close {
        transition: none;
      }
    }
  `]
})
export class ToastContainerComponent implements AfterViewInit, OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly hostElement = inject(ElementRef<HTMLElement>);

  readonly toastService = inject(ToastService);
  readonly positions: ToastPosition[] = [
    'top-right',
    'top-left',
    'bottom-right',
    'bottom-left',
    'top-center',
    'bottom-center'
  ];
  readonly toastsByPosition = computed(() => {
    const groups = {
      'top-right': [] as Toast[],
      'top-left': [] as Toast[],
      'bottom-right': [] as Toast[],
      'bottom-left': [] as Toast[],
      'top-center': [] as Toast[],
      'bottom-center': [] as Toast[]
    };

    for (const toast of this.toastService.toasts$()) {
      groups[toast.position ?? 'top-right'].push(toast);
    }

    return groups;
  });

  ngAfterViewInit(): void {
    const host = this.hostElement.nativeElement;
    if (host.parentElement !== this.document.body) {
      this.document.body.appendChild(host);
    }
  }

  ngOnDestroy(): void {
    const host = this.hostElement.nativeElement;
    if (host.parentElement === this.document.body) {
      this.document.body.removeChild(host);
    }
  }
}
